import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Paper, Typography, IconButton, Collapse, Chip } from '@mui/material';
import { BugReport, Close, CheckCircle, Warning } from '@mui/icons-material';

/**
 * SEO Debugger - Component to verify SEO metadata
 *
 * Controlled by window.ENV.SEO_DEBUGGER_ENABLED (set in public/config.js)
 * Set to 'true' to enable, anything else to disable.
 *
 * Usage: Add to App.tsx or any page:
 * ```tsx
 * <SEODebugger />
 * ```
 */
export const SEODebugger: React.FC = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [seoData, setSeoData] = useState<any>(null);

  useEffect(() => {
    const extractSEOData = () => {
      const data: any = {
        title: document.querySelector('title')?.textContent || 'NOT FOUND',
        description: document.querySelector('meta[name="description"]')?.getAttribute('content') || 'NOT FOUND',
        canonical: document.querySelector('link[rel="canonical"]')?.getAttribute('href') || 'NOT FOUND',
        robots: document.querySelector('meta[name="robots"]')?.getAttribute('content') || 'index, follow (default)',

        openGraph: {
          title: document.querySelector('meta[property="og:title"]')?.getAttribute('content') || 'NOT FOUND',
          description: document.querySelector('meta[property="og:description"]')?.getAttribute('content') || 'NOT FOUND',
          url: document.querySelector('meta[property="og:url"]')?.getAttribute('content') || 'NOT FOUND',
          image: document.querySelector('meta[property="og:image"]')?.getAttribute('content') || 'NOT FOUND',
          type: document.querySelector('meta[property="og:type"]')?.getAttribute('content') || 'NOT FOUND',
        },

        twitter: {
          card: document.querySelector('meta[name="twitter:card"]')?.getAttribute('content') || 'NOT FOUND',
          title: document.querySelector('meta[name="twitter:title"]')?.getAttribute('content') || 'NOT FOUND',
          description: document.querySelector('meta[name="twitter:description"]')?.getAttribute('content') || 'NOT FOUND',
          image: document.querySelector('meta[name="twitter:image"]')?.getAttribute('content') || 'NOT FOUND',
        },

        structuredData: Array.from(document.querySelectorAll('script[type="application/ld+json"]'))
          .map((script) => {
            try {
              return JSON.parse(script.textContent || '{}');
            } catch {
              return { error: 'Failed to parse JSON-LD' };
            }
          }),
      };

      setSeoData(data);
    };

    // Wait longer for React Helmet to inject tags (increased for lazy-loaded components)
    // Lazy-loaded routes need extra time to load before SEO tags are injected
    const timer = setTimeout(extractSEOData, 1500);
    return () => clearTimeout(timer);
  }, [location]); // Re-extract SEO data whenever route changes

  // Only show if SEO_DEBUGGER_ENABLED is set to 'true' in window.ENV
  if (!(window as any).ENV?.SEO_DEBUGGER_ENABLED || (window as any).ENV.SEO_DEBUGGER_ENABLED !== 'true') {
    return null;
  }

  const getStatus = (value: string) => {
    if (value === 'NOT FOUND' || !value) {
      return <Chip label="Missing" color="error" size="small" />;
    }
    if (value.length > 160 && value !== seoData?.title) {
      return <Chip label="Too Long" color="warning" size="small" />;
    }
    return <Chip label="OK" color="success" size="small" />;
  };

  return (
    <>
      {/* Floating Debug Button */}
      <IconButton
        onClick={() => setOpen(!open)}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          bgcolor: 'primary.main',
          color: 'white',
          zIndex: 9999,
          '&:hover': {
            bgcolor: 'primary.dark',
          },
        }}
      >
        {open ? <Close /> : <BugReport />}
      </IconButton>

      {/* Debug Panel */}
      <Collapse in={open}>
        <Paper
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            maxWidth: 600,
            maxHeight: '70vh',
            overflow: 'auto',
            p: 2,
            zIndex: 9998,
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="h6" gutterBottom>
            🔍 SEO Debugger
          </Typography>

          {!seoData ? (
            <Typography>Loading...</Typography>
          ) : (
            <Box>
              {/* Title */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2">Title ({seoData.title.length} chars)</Typography>
                  {getStatus(seoData.title)}
                </Box>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {seoData.title}
                </Typography>
              </Box>

              {/* Description */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2">Description ({seoData.description.length} chars)</Typography>
                  {getStatus(seoData.description)}
                </Box>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {seoData.description}
                </Typography>
              </Box>

              {/* Canonical */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2">Canonical URL</Typography>
                  {getStatus(seoData.canonical)}
                </Box>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {seoData.canonical}
                </Typography>
              </Box>

              {/* Robots */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2">Robots</Typography>
                  {seoData.robots.includes('noindex') ? (
                    <Chip label="No Index" color="warning" size="small" />
                  ) : (
                    <Chip label="Indexable" color="success" size="small" />
                  )}
                </Box>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {seoData.robots}
                </Typography>
              </Box>

              {/* Open Graph */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Open Graph (Facebook/LinkedIn)
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="caption" display="block">
                    Title: {getStatus(seoData.openGraph.title)} {seoData.openGraph.title}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Description: {getStatus(seoData.openGraph.description)}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Image: {getStatus(seoData.openGraph.image)} {seoData.openGraph.image}
                  </Typography>
                  <Typography variant="caption" display="block">
                    URL: {getStatus(seoData.openGraph.url)}
                  </Typography>
                </Box>
              </Box>

              {/* Twitter */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Twitter Card
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="caption" display="block">
                    Card: {getStatus(seoData.twitter.card)} {seoData.twitter.card}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Title: {getStatus(seoData.twitter.title)}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Image: {getStatus(seoData.twitter.image)}
                  </Typography>
                </Box>
              </Box>

              {/* Structured Data */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Structured Data ({seoData.structuredData.length} schemas)
                </Typography>
                {seoData.structuredData.length === 0 ? (
                  <Typography variant="caption" color="warning.main">
                    No structured data found
                  </Typography>
                ) : (
                  seoData.structuredData.map((schema: any, index: number) => (
                    <Box key={index} sx={{ pl: 2, mb: 1 }}>
                      <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                        Schema {index + 1}: {schema['@type'] || schema['@graph'] ? '@graph' : 'Unknown'}
                      </Typography>
                      <Typography
                        variant="caption"
                        component="pre"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.65rem',
                          bgcolor: 'grey.100',
                          p: 1,
                          borderRadius: 1,
                          overflow: 'auto',
                          maxHeight: 150,
                        }}
                      >
                        {JSON.stringify(schema, null, 2)}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </Collapse>
    </>
  );
};
